import React, { FC, Fragment, useState } from 'react';
import { Box, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import {
    ProductionOverviewCellInput,
    ProductionOverviewCountMode,
    ProductionOverviewRowKey,
} from 'models/production-overview';
import {
    OverviewCalendarGroup,
    OverviewCalendarRow,
    buildRowCellInput,
    getHeatColor,
    getTodayKey,
    isMonday,
    isWeekendDay,
    pickCount,
    sumGroupCountsForDay,
} from './production-overview-utils';

export type OverviewCellClickHandler = (labelParts: string[], cells: ProductionOverviewCellInput[]) => void;

type OverviewGroupRowProps = {
    group: OverviewCalendarGroup;
    days: string[];
    countMode: ProductionOverviewCountMode;
    heatMaxCount: number;
    isCompact: boolean;
    onCellClick: OverviewCellClickHandler;
};

const dayCellBorders = (dayKey: string, isCompact: boolean) => ({
    borderLeft: isMonday(dayKey) ? '2px solid #ccc' : undefined,
    paddingX: isCompact ? 0.25 : 0.5,
    paddingY: 0.5,
});

type CountCellProps = {
    dayKey: string;
    count: number;
    lateCount?: number;
    heatMaxCount: number;
    isCompact: boolean;
    isHeader?: boolean;
    onClick?: () => void;
    onLateClick?: () => void;
};

const CountCell: FC<CountCellProps> = ({
    dayKey,
    count,
    lateCount,
    heatMaxCount,
    isCompact,
    isHeader,
    onClick,
    onLateClick,
}: CountCellProps) => {
    const { t: translate } = useTranslation(['production-overview']);
    const isToday = dayKey === getTodayKey();
    const heatColor = count > 0 ? getHeatColor(count, heatMaxCount) : undefined;
    const weekendColor = isWeekendDay(dayKey) ? '#f5f5f5' : undefined;

    return (
        <TableCell
            align="center"
            onClick={count > 0 ? onClick : undefined}
            sx={{
                ...dayCellBorders(dayKey, isCompact),
                backgroundColor: heatColor || weekendColor || 'transparent',
                cursor: count > 0 && onClick ? 'pointer' : 'default',
                outline: isToday ? '2px solid #1e88e5' : undefined,
                outlineOffset: isToday ? '-2px' : undefined,
            }}
        >
            {count > 0 ? (
                <Typography
                    variant="body2"
                    fontWeight={isHeader ? 600 : 500}
                    fontSize={isCompact ? '0.7rem' : undefined}
                >
                    {count}
                </Typography>
            ) : undefined}
            {lateCount ? (
                <Typography
                    component="div"
                    onClick={(event) => {
                        event.stopPropagation();
                        onLateClick?.();
                    }}
                    sx={{
                        fontSize: isCompact ? '0.6rem' : '0.65rem',
                        color: '#d32f2f',
                        fontWeight: 600,
                        cursor: onLateClick ? 'pointer' : 'default',
                    }}
                >
                    {translate('production-overview:LATE_COUNT', { count: lateCount })}
                </Typography>
            ) : undefined}
        </TableCell>
    );
};

const OverviewGroupRow: FC<OverviewGroupRowProps> = ({
    group,
    days,
    countMode,
    heatMaxCount,
    isCompact,
    onCellClick,
}: OverviewGroupRowProps) => {
    const { t: translate } = useTranslation(['production-overview']);
    const [isExpanded, setIsExpanded] = useState(true);
    const todayKey = getTodayKey();
    const groupLabel = translate(`production-overview:${group.labelKey}`);

    const labelCellStyle = {
        position: 'sticky' as const,
        left: 0,
        backgroundColor: '#fff',
        zIndex: 1,
        whiteSpace: 'nowrap' as const,
    };

    const handleGroupTotalClick = (dayKey: string) => {
        if (group.headerRowKey) {
            onCellClick([groupLabel, todayKey], [{ rowKey: group.headerRowKey }]);
            return;
        }
        if (group.groupTotalRowKey) {
            onCellClick([groupLabel, dayKey], [{ rowKey: group.groupTotalRowKey, day: dayKey }]);
            return;
        }
        const cells = group.rows
            .filter((row) => !row.pointInTimeOnly || dayKey === todayKey)
            .map((row) => buildRowCellInput(row, dayKey));
        onCellClick([groupLabel, dayKey], cells);
    };

    const headerCountForDay = (dayKey: string): number => {
        if (group.pointInTimeHeaderCount) {
            return dayKey === todayKey ? pickCount(group.pointInTimeHeaderCount, countMode) : 0;
        }
        return pickCount(sumGroupCountsForDay(group, dayKey), countMode);
    };

    const handleRowCellClick = (row: OverviewCalendarRow, dayKey: string) => {
        const rowLabel = translate(`production-overview:${row.labelKey}`);
        onCellClick([groupLabel, rowLabel, row.pointInTimeOnly ? todayKey : dayKey], [buildRowCellInput(row, dayKey)]);
    };

    const handleLateTodayClick = (row: OverviewCalendarRow) => {
        const rowLabel = translate(`production-overview:${row.labelKey}`);
        onCellClick(
            [groupLabel, rowLabel, translate('production-overview:LATE')],
            [{ rowKey: ProductionOverviewRowKey.LATE_TODAY }],
        );
    };

    const rowCount = (row: OverviewCalendarRow, dayKey: string): number => {
        if (row.pointInTimeOnly && dayKey !== todayKey) return 0;
        return pickCount(row.countsByDay.get(dayKey), countMode);
    };

    return (
        <Fragment>
            <TableRow data-test={`production-overview.group-${group.groupKey}`}>
                <TableCell sx={labelCellStyle}>
                    <Box display="flex" alignItems="center">
                        {group.rows.length ? (
                            <IconButton
                                size="small"
                                onClick={() => setIsExpanded(!isExpanded)}
                                aria-label={groupLabel}
                                data-test={`production-overview.toggle-group-${group.groupKey}`}
                            >
                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        ) : undefined}
                        <Typography variant="body2" fontWeight={600}>
                            {groupLabel}
                        </Typography>
                    </Box>
                </TableCell>
                {days.map((dayKey) =>
                    group.showDayTotals ? (
                        <CountCell
                            key={dayKey}
                            dayKey={dayKey}
                            count={headerCountForDay(dayKey)}
                            heatMaxCount={heatMaxCount}
                            isCompact={isCompact}
                            isHeader
                            onClick={() => handleGroupTotalClick(dayKey)}
                        />
                    ) : (
                        <TableCell key={dayKey} sx={dayCellBorders(dayKey, isCompact)} />
                    ),
                )}
            </TableRow>

            {isExpanded
                ? group.rows.map((row) => (
                      <TableRow key={row.rowKey} data-test={`production-overview.row-${row.rowKey}`}>
                          <TableCell sx={{ ...labelCellStyle, paddingLeft: 5 }}>
                              <Typography variant="body2" fontSize={isCompact ? '0.75rem' : undefined}>
                                  {translate(`production-overview:${row.labelKey}`)}
                              </Typography>
                          </TableCell>
                          {days.map((dayKey) => (
                              <CountCell
                                  key={dayKey}
                                  dayKey={dayKey}
                                  count={rowCount(row, dayKey)}
                                  lateCount={
                                      dayKey === todayKey && row.lateToday
                                          ? pickCount(row.lateToday, countMode)
                                          : undefined
                                  }
                                  heatMaxCount={heatMaxCount}
                                  isCompact={isCompact}
                                  onClick={() => handleRowCellClick(row, dayKey)}
                                  onLateClick={() => handleLateTodayClick(row)}
                              />
                          ))}
                      </TableRow>
                  ))
                : undefined}
        </Fragment>
    );
};

export default OverviewGroupRow;
